import { memo, useEffect } from "react";
import { useSelector } from "react-redux";
import { makeSelectUsersById } from "modules/event/usersInEvent/selectors";
import { Map } from "immutable";
import { useI18n } from "i18n";
import { IRoom } from "types";
import { IUser } from "modules/app/types";
import { playEnteredRoomSound } from "helpers/audioHelper";
import { usePrevious } from "helpers/reactHooksUtils";
import { getUserName } from "modules/userProfile";
import { selectUser } from "modules/auth/redux/selectors";
import { selectIsBroadcasting } from "../../broadcast/redux/selectors";
import useNotificationActions from "../../notification/hooks/useNotificationActions";
import { makeSelectUserRoomStatusByRoomId } from "../../room/redux/selectors";

interface Props {
  room: IRoom;
}

const MyRoom = memo(({ room }: Props) => {
  const { t } = useI18n(["template"]);
  const roomId = room.id;
  const usersInside = useSelector(makeSelectUserRoomStatusByRoomId(roomId));
  const userDetails = useSelector(
    makeSelectUsersById(Object.keys(usersInside || {})),
  );
  const { addInfoNotification } = useNotificationActions();
  const user = useSelector(selectUser);
  const prevUsersInside = usePrevious(usersInside);
  const prevUserDetails = useSelector(
    makeSelectUsersById(Object.keys(prevUsersInside || {})),
  );
  const isBroadcasting = useSelector(selectIsBroadcasting);

  const getNames = (userIds: string[], details: Map<string, IUser>) =>
    userIds
      .map((id) => getUserName(details.get(id)))
      .filter((name) => !!name)
      .join(",");

  useEffect(() => {
    // Check if any new user have joined or user have left room, show notifications to other users in room only in speaker and tile view
    if (
      prevUsersInside &&
      Object.keys(prevUsersInside).length &&
      usersInside &&
      Object.keys(usersInside).length
    ) {
      const prevMembersIds = Object.keys(prevUsersInside);
      const newMembersIds = Object.keys(usersInside);

      const newMembers = newMembersIds.filter(
        (mem: string) =>
          user &&
          mem !== user.id &&
          !prevMembersIds.some((prevMem: string) => prevMem === mem),
      );
      const leftMembers = prevMembersIds.filter(
        (mem: string) =>
          user &&
          mem !== user.id &&
          !newMembersIds.some((prevMem: string) => prevMem === mem),
      );

      if (!isBroadcasting && newMembers.length) {
        playEnteredRoomSound();

        if (userDetails) {
          addInfoNotification({
            message: t("template:entry.table.text", {
              nameKey: `${getNames(newMembers, userDetails)}`,
            }),
            position: "tr",
          });
        }
      }

      if (!isBroadcasting && leftMembers.length && prevUserDetails) {
        addInfoNotification({
          message: t("template:exit.table.text", {
            nameKey: `${getNames(leftMembers, prevUserDetails)}`,
          }),
          position: "tr",
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersInside]);

  return null;
});

export default MyRoom;
